// Shared behaviour for the content edit pages (EditProcess, EditTimeline, …): loading the node, the
// Details drawer fields, unsaved-change tracking, Preview, the save dialog, leave guards and the
// outline's activity. Each page overrides the content hooks at the bottom (contentField etc.) and
// calls guardLeave() from its own beforeRouteLeave (vue-router doesn't read route guards from mixins).
import { defineComponent } from 'vue';
import axios from 'axios';
import { publicUsersForNode } from '@/nodeViewers';

export function parseList(json: any): any[] {
    if(!json) return [];
    try {
        return JSON.parse(json) || [];
    } catch(e) {
        return [];
    }
}

export default defineComponent({
    data() {
        return {
            node: {} as any,
            isLoaded: false,
            // Details drawer, written back to the node's JSON columns on save
            locs: [] as string[],
            deps: [] as string[],
            types: [] as string[],
            ownerIDs: [] as number[],
            savedSnapshot: '',
            saved: false,
            disableButton: false,
            showSave: false,
            showDiscard: false,
            showDetails: false,
            showPreview: false,
            previewNode: null as any,
            barHeight: 0,
            saveOptions: { minorChange: false, changeNote: '' } as any,
            comments: [] as any[],
            signOffs: [] as any[],
            publicUsers: [] as any[],
            showAIConfirm: false,
            AIConfirmText: ''
        }
    },
    computed: {
        snapshot(): string {
            return JSON.stringify({
                name: this.node.Name,
                content: this.contentSnapshot(),
                locs: [...this.locs].sort(),
                deps: [...this.deps].sort(),
                types: [...this.types].sort(),
                owners: [...this.ownerIDs],
                layout: this.node.ProcessLayout || null // processes: the details drawer's Layout (EditProcess.vue)
            });
        },
        dirty(): boolean {
            return !this.saved && this.isLoaded && this.snapshot != this.savedSnapshot;
        },
        ownerNames(): string {
            const users = this.$store.state.users || [];
            return this.ownerIDs
                .map(id => users.find((u: any) => u.UserID == id))
                .filter(Boolean)
                .map((u: any) => `${u.FirstName} ${u.LastName}`)
                .join(', ');
        },
        // Same rule as the sign-off rail: public users who can view it, signed since the last edit
        requiredSigners(): any[] {
            return publicUsersForNode(this.publicUsers, parseList(this.node.LocationsJSON), parseList(this.node.DepartmentsJSON));
        },
        signedCount(): number {
            const modifiedAt = this.node.LastModified ? new Date(this.node.LastModified).getTime() : 0;
            const signed = this.signOffs
                .filter((s: any) => new Date(s.Date).getTime() >= modifiedAt)
                .map((s: any) => s.Phone);
            return this.requiredSigners.filter((u: any) => signed.includes(u.Phone)).length;
        }
    },
    async created() {
        if(!this.$route.params.node) {
            return;
        }

        await axios.get(`${this.$store.state.apiUrl}/api/playbook/node/${this.$route.params.node}`)
            .then(resp => {
                this.node = resp.data;
            })
            .catch(e => {
                console.log(e);
            })

        if(!this.node || !this.node.Slug) {
            return;
        }

        this.loadContent(JSON.parse(this.node[this.contentField()]));
        this.locs = parseList(this.node.LocationsJSON);
        this.deps = parseList(this.node.DepartmentsJSON);
        this.types = parseList(this.node.TypesJSON);
        this.ownerIDs = parseList(this.node.ProcessOwnersJSON);
        this.markSaved();

        this.$store.state.routeLoaded = true;
        this.isLoaded = true;
        this.getActivity();
        // ?ai=1 ("Improve with AI" on the reading page): open the editor's AI panel straight away
        if (this.$route.query.ai) {
            const query: any = { ...this.$route.query };
            delete query.ai;
            this.$router.replace({ query });
            this.$nextTick(() => { if (typeof (this as any).openAIEdit == 'function') (this as any).openAIEdit(); else (this as any).showAIModal = true; });
        }
    },
    mounted() {
        window.addEventListener('beforeunload', this.warnUnload);
    },
    beforeUnmount() {
        window.removeEventListener('beforeunload', this.warnUnload);
    },
    methods: {
        // ---- Content hooks: each edit page overrides these ----
        // The node column holding the page's JSON, e.g. 'ProcessJSON'
        contentField(): string {
            return '';
        },
        // Parsed JSON from contentField, on load
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        loadContent(json: any) {
            // overridden
        },
        // The JSON object to save back to contentField
        contentData(): any {
            return {};
        },
        // What counts as a change to the content (normalized so false vs. missing doesn't count)
        contentSnapshot(): any {
            return null;
        },
        // Called after a load or save so the page can remember the saved content (e.g. for Preview markers)
        onMarkSaved() {
            // overridden
        },
        // Last tidy-up of the content before it's saved (e.g. adding https:// to links)
        beforeSave() {
            // overridden
        },
        // Other node columns to save alongside the content (e.g. SheetColsJSON)
        extraFields(): any {
            return {};
        },
        // Title-case type name for messages, e.g. 'Process', 'Value Engine'
        typeTitle(): string {
            return 'Item';
        },

        // ---- Shared ----
        markSaved() {
            this.savedSnapshot = this.snapshot;
            this.onMarkSaved();
        },
        // The node as it would be saved: content plus the details drawer
        nodeWithEdits() {
            const typesJSON = this.types.length || this.node.TypesJSON ? JSON.stringify(this.types) : this.node.TypesJSON;
            return {
                ...JSON.parse(JSON.stringify(this.node)),
                [this.contentField()]: JSON.stringify(this.contentData()),
                LocationsJSON: JSON.stringify(this.locs),
                DepartmentsJSON: JSON.stringify(this.deps),
                TypesJSON: typesJSON,
                ProcessOwnersJSON: JSON.stringify(this.ownerIDs),
                ...this.extraFields()
            };
        },
        // Renders the unsaved edits in Playbook.vue (same display as the live page) without saving
        openPreview() {
            this.previewNode = this.nodeWithEdits();
            this.showPreview = true;
            (this.$el as HTMLElement).scrollTop = 0; // .page-wrap is the scroller
        },
        closePreview() {
            this.showPreview = false;
        },
        openSave() {
            this.showSave = true;
        },
        // From the save dialog: { minorChange, changeNote }
        submitCheck(options: any) {
            this.saveOptions = options || this.saveOptions;
            if(!`${this.node.Name || ''}`.trim()) {
                alert('Include a name (Edit details)');
                return;
            }
            this.node.Name = this.node.Name.trim();
            if(!this.locs.length) {
                alert('Include at least one location (Edit details)');
                return;
            }
            if(!this.deps.length) {
                alert('Include at least one department (Edit details)');
                return;
            }
            if(!this.node.NeedsApprovalType) {
                const placeholders = (JSON.stringify(this.contentData()).match(/\[confirm:/g) || []).length;
                if(placeholders) {
                    this.AIConfirmText = `This ${this.typeTitle().toLowerCase()} still contains ${placeholders} [confirm: ...] placeholder(s) that should be filled in before publishing. Publish anyway?`;
                    this.showAIConfirm = true;
                    return;
                }
            }
            this.submit();
        },
        async submit() {
            if(this.disableButton) {
                return;
            }
            this.showAIConfirm = false;
            this.beforeSave();
            const data: any = this.nodeWithEdits();
            data.changeNote = this.saveOptions.changeNote;
            data.minorChange = this.saveOptions.minorChange;
            if(!data.NeedsApprovalType) {
                data.publishApproved = true;
            }

            this.disableButton = true;

            await axios.post(`${this.$store.state.apiUrl}/api/playbook/node`, data)
                .then(() => {
                    this.$store.dispatch('setStoreData');
                    alert(`${this.typeTitle()} updated!`);
                    this.showSave = false;
                    this.saved = true;
                    this.$router.push('/playbook/' + this.node.Slug);
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error')
                })

            this.disableButton = false;
        },
        cancel() {
            if(this.dirty) {
                this.showDiscard = true;
                return;
            }
            this.leave();
        },
        leave() {
            this.showDiscard = false;
            this.saved = true; // skips the leave prompt
            this.$router.push('/playbook/' + this.node.Slug);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        guardLeave(next: any) {
            if(this.dirty && !window.confirm('You have unsaved changes. Leave without saving?')) {
                next(false);
                return;
            }
            next();
        },
        warnUnload(e: any) {
            if(this.dirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        },
        // Uploads an image the way the process/value engine editors always have; resolves to its URL
        async uploadImage(file: any): Promise<string | null> {
            if(!file) return null;
            const fileNameLower = file.name.toLowerCase();
            if(!fileNameLower.includes('.jpg') &&
                !fileNameLower.includes('.jpeg') &&
                !fileNameLower.includes('.png') &&
                !fileNameLower.includes('.gif')) {
                    alert('File is not an image');
                    return null;
            }
            const formData = new FormData();
            formData.append('file', file);
            return axios.post(`${this.$store.state.apiUrl}/api/playbook/processimage`, formData)
                .then(resp => this.$store.state.spacesUrl + resp.data.key)
                .catch(e => {
                    console.log(e);
                    alert("There was an error.");
                    return null;
                });
        },
        getActivity() {
            const slug = this.node.Slug;
            axios.get(`${this.$store.state.apiUrl}/api/playbook/comments/${slug}`)
                .then(resp => { this.comments = resp.data; })
                .catch(e => { console.log(e); })
            axios.get(`${this.$store.state.apiUrl}/api/signoffs/${slug}`)
                .then(resp => { this.signOffs = resp.data; })
                .catch(e => { console.log(e); })
            axios.get(`${this.$store.state.apiUrl}/api/publicusers`)
                .then(resp => { this.publicUsers = resp.data; })
                .catch(e => { console.log(e); })
        }
    }
});
